import { Link } from "react-router-dom";
import { CalendarDays, CheckSquare, ClipboardList, FileSignature, FolderKanban, Target, TrendingUp } from "lucide-react";
import { usePartner } from "../PartnerContext";
import { Card, PageHeader, SectionTitle } from "../ui";
import { euro } from "@/data/partnerConfig";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card className="p-5" testid={testid}>
    <Icon className="h-5 w-5 text-cognac" />
    <p className="mt-5 font-display text-3xl font-medium text-navy">{value}</p>
    <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-stone-400">{label}</p>
  </Card>
);

const SalesDashboard = ({ data }) => {
  const p = data.performance || {};
  return (
    <div data-testid="partner-dashboard-sales">
      <PageHeader eyebrow="Mon activité commerciale" title="Mes performances" sub="Uniquement vos prospects, vos signatures et vos résultats personnels." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Mes prospects" value={p.prospects || 0} icon={Target} testid="sales-own-prospects" />
        <Stat label="Mes rendez-vous" value={p.appointments || 0} icon={CalendarDays} testid="sales-own-appointments" />
        <Stat label="Mes signatures" value={p.signatures || 0} icon={FileSignature} testid="sales-own-signatures" />
        <Stat label="Mon taux de transformation" value={`${p.conversion_rate || 0} %`} icon={TrendingUp} testid="sales-own-conversion" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-5" testid="sales-own-revenue"><SectionTitle>Mes ventes</SectionTitle><p className="font-display text-3xl text-navy">{euro(p.personal_revenue || 0)}</p><p className="mt-2 text-xs text-stone-500">Indicateur personnel — aucun CA global n’est affiché.</p></Card>
        <Card className="p-5"><SectionTitle>Mes prochaines actions</SectionTitle><div className="space-y-2">{(p.actions || []).map((a) => <Link key={a.id} to="/partenaire/prospects" className="flex items-center gap-3 border-b border-stone-100 py-2 text-sm last:border-0" data-testid={`sales-action-${a.id}`}><CheckSquare className="h-4 w-4 text-sage" /><span><b>{a.prospect}</b> · {a.label}</span></Link>)}</div></Card>
      </div>
    </div>
  );
};

const AssistantDashboard = ({ data }) => (
  <div data-testid="partner-dashboard-assistant">
    <PageHeader eyebrow="Suivi administratif" title="Dossiers et échéances" sub="Les informations utiles à votre mission, sans données financières sensibles." />
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5"><SectionTitle>Rendez-vous</SectionTitle>{(data.appointments || []).map((t) => <p key={t.id} className="flex gap-3 border-b border-stone-100 py-3 text-sm" data-testid={`assistant-appointment-${t.id}`}><CalendarDays className="h-4 w-4 text-cognac" />{t.label}</p>)}</Card>
      <Card className="p-5"><SectionTitle>Dossiers à compléter</SectionTitle>{(data.admin_tasks || []).map((t) => <p key={t.id} className="flex gap-3 border-b border-stone-100 py-3 text-sm" data-testid={`assistant-task-${t.id}`}><ClipboardList className="h-4 w-4 text-petrol" />{t.label}</p>)}</Card>
    </div>
  </div>
);

const SiteDashboard = ({ data }) => (
  <div data-testid="partner-dashboard-site-manager">
    <PageHeader eyebrow="Suivi chantier" title="Mes chantiers attribués" sub="Planning, interventions, observations et avancements utiles à votre mission." />
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {(data.projects || []).map((p) => <Link key={p.id} to={`/partenaire/projets/${p.reference}`} data-testid={`site-project-${p.reference}`}><Card className="h-full p-5 transition-colors hover:border-petrol/40"><FolderKanban className="h-5 w-5 text-cognac" /><p className="mt-5 font-mono text-xs font-bold text-petrol">{p.reference}</p><h2 className="mt-2 font-display text-xl text-navy">{p.name}</h2><p className="mt-3 text-sm text-stone-500">{p.phase}</p></Card></Link>)}
    </div>
  </div>
);

export default function PartnerRoleDashboard() {
  const { dashboard } = usePartner();
  if (dashboard.view === "SALES") return <SalesDashboard data={dashboard} />;
  if (dashboard.view === "ASSISTANT") return <AssistantDashboard data={dashboard} />;
  return <SiteDashboard data={dashboard} />;
}